import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'; import L from 'leaflet';
import { Toaster, toast } from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import './App.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { isHiddenFaultOwnershipType } from './utils/faultVisibility';
import { matchesCollaborationSelection } from './constants/companies';

const isMobile = () => window.matchMedia("(max-width: 768px)").matches;
const API_BASE = process.env.REACT_APP_API_URL;

const DEFAULT_OWNERSHIP = {
  iskent: false,
  belediye: true,
  aksa: false,
  hatsu: false,
  enerjisa: false,
  turktelekom: false,
  superonline: false,
  vodafone: false,
  insaat: false
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});


const sleep = (ms = 0) => new Promise(r => setTimeout(r, ms));

const LAYER_ICON = {
  satellite: (process.env.PUBLIC_URL || '') + '/img/layers-street.png',
  street: (process.env.PUBLIC_URL || '') + '/img/layers-satellite.png',
};


const loadGlifyScript = () => {
  return new Promise((resolve) => {
    if (window.L && window.L.glify) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet.glify@3.3.1/dist/glify-browser.js';
    script.onload = () => {
      if (window.L && window.L.glify) {
        window.L.glify.longitudeFirst();
      }
      resolve();
    };
    document.head.appendChild(script);
  });
};

const purplePinIcon = L.icon({
  iconUrl: '/img/purple-pin.png',
  iconSize: [32, 32],
  iconAnchor: [12, 12],
  popupAnchor: [0, -32]
});

const redPinIcon = L.icon({
  iconUrl: '/img/red-pin.png',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -32]
});

const orangePinIcon = L.icon({
  iconUrl: '/img/orange-pin.png',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -32]
});

const greenPinIcon = L.icon({
  iconUrl: '/img/green-pin.png',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -32]
});

const insaatPinIcon = L.divIcon({
  html: `<div style="background-color: #d97706; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
           <i class="fas fa-hard-hat" style="color: white; font-size: 16px;"></i>
         </div>`,
  className: 'custom-div-icon',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const TYPE_DISPLAY_NAMES = {
  BILDIRILENLER: 'BİLDİRİLENLER',
  'BİLDİRİLENLER': 'BİLDİRİLENLER',
  BILDIRILEN: 'BİLDİRİLENLER',
  BILDIRİLEN: 'BİLDİRİLENLER',
  'BİLDİRİLEN': 'BİLDİRİLENLER',
  'Insaat': 'İNŞAAT',
  'Park': 'PARK'
};

const getTypeDisplayName = (type) => {
  if (!type) return type;
  return TYPE_DISPLAY_NAMES[type] || type;
};

function normalize(str = '') {
  return str
    .toLocaleLowerCase('tr-TR')
    .replace(/ğ/g, 'g')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ı/g, 'i');
}

function getSearchTokens(raw) {
  return normalize(raw)
    .split(/\s+/)
    .filter(t => t);
}

function matchItemByTokens(item, tokens) {
  if (tokens.length === 0) return true;
  const mahalleNorm = normalize(item.Mahalle);
  const idStr = String(item.ID ?? '');

  return tokens.every(tok => {
    if (/^\d+$/.test(tok)) {
      return idStr.includes(tok);
    }
    return mahalleNorm.includes(tok);
  });
}

function passesColorFilter(item, colorFilters) {
  const color = item.IsFixed === 4 ? 'purple' :
    item.IsFixed === 2 ? 'green' :
      item.IsFixed === 1 ? 'orange' : 'red';
  return !!colorFilters[color];
}

function getItemOwnerId(item) {
  const raw = item?.CreatedById ?? item?.createdById ?? item?.createdBy ?? item?.CreatedBy ?? item?.UserId ?? item?.userId ?? null;
  if (!raw) return null;
  return String(raw).trim().toLowerCase();
}

function passesOwnershipFilter(item, ownershipSelection = DEFAULT_OWNERSHIP) {
  if (isHiddenFaultOwnershipType(item)) return false;
  // Hide Baskan requests (OwnershipType 10) from the main map
  if (item.FaultOwnershipType === 10 || item.faultOwnershipType === 10) {
    return false;
  }
  // Also check string representation in CreatedByUserOwnershipType just in case
  const rawOwner = (item?.CreatedByUserOwnershipType ?? item?.createdByUserOwnershipType ?? '').trim();
  if (rawOwner === '10' || rawOwner.toLowerCase() === 'baskan') {
    return false;
  }

  const isSpeciallyFiltered = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;

  const selection = ownershipSelection || DEFAULT_OWNERSHIP;
  const { belediye, iskent, aksa, hatsu, enerjisa, turktelekom, superonline, vodafone, insaat } = selection;

  if (isSpeciallyFiltered) {
    return !!insaat;
  }
  if (matchesCollaborationSelection(item, selection)) return true;

  if ((belediye && iskent && aksa && hatsu && enerjisa && turktelekom && superonline && vodafone) || (!belediye && !iskent && !aksa && !hatsu && !enerjisa && !turktelekom && !superonline && !vodafone)) {
    // If no main filter is active, but a special filter (insaat) is active, hide main points
    if (insaat) return false;
    return true;
  }

  const ownershipType = (rawOwner || 'Belediye').trim();
  const normalizedType = ownershipType.toLowerCase();

  const isBelediye = normalizedType === 'belediye' || !ownershipType;
  const isIskent = normalizedType === 'iskent';
  const isAksa = normalizedType === 'aksa';
  const isHatsu = normalizedType === 'hatsu';
  const isEnerjisa = normalizedType === 'enerjisa';
  const isTurktelekom = normalizedType === 'turktelekom';
  const isSuperonline = normalizedType === 'superonline';
  const isVodafone = normalizedType === 'vodafone';

  let match = false;
  if (belediye && isBelediye) match = true;
  if (iskent && isIskent) match = true;
  if (aksa && isAksa) match = true;
  if (hatsu && isHatsu) match = true;
  if (enerjisa && isEnerjisa) match = true;
  if (turktelekom && isTurktelekom) match = true;
  if (superonline && isSuperonline) match = true;
  if (vodafone && isVodafone) match = true;

  return match;
}

function filterItemsForView(items, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows = []) {
  const hasQuery = tokens.length > 0;
  const combined = [...items, ...insaatRows];

  return combined.filter(item => {
    const matchesSearch = matchItemByTokens(item, tokens);
    const inSelection = selectedMahalles.size > 0 && selectedMahalles.has(item.Mahalle);

    const shouldShow = hasQuery ? (matchesSearch && inSelection) : inSelection;
    const isSpecial = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;

    return shouldShow && (isSpecial || passesColorFilter(item, colorFilters)) && passesOwnershipFilter(item, ownershipSelection);
  });
}

function filterItemsForViewWithFallback(items, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows = []) {
  const selectionEmpty = selectedMahalles.size === 0;
  const hasQuery = tokens.length > 0;
  const combined = [...items, ...insaatRows];

  return combined.filter(item => {
    const matchesSearch = matchItemByTokens(item, tokens);
    const inSelection = selectionEmpty ? true : selectedMahalles.has(item.Mahalle);
    const shouldShow = hasQuery ? (matchesSearch && inSelection) : inSelection;
    const isSpecial = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;

    return shouldShow && (isSpecial || passesColorFilter(item, colorFilters)) && passesOwnershipFilter(item, ownershipSelection);
  });
}

const App = () => {
  const [data, setData] = useState([]);
  const [insaatRows, setInsaatRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const allMahallesSet = useMemo(
    () => new Set([...data, ...insaatRows].map(d => d.Mahalle)),
    [data, insaatRows]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllPins, setShowAllPins] = useState(false);
  const [selectedMahalles, setSelectedMahalles] = useState(new Set());
  const [selectedType, setSelectedType] = useState(null);
  const [selectedYamaType, setSelectedYamaType] = useState(null);
  const [ownershipSelection, setOwnershipSelection] = useState(DEFAULT_OWNERSHIP);
  const ownershipSelectionRef = useRef(ownershipSelection);

  useEffect(() => {
    ownershipSelectionRef.current = ownershipSelection;
  }, [ownershipSelection]);

  const [colorFilters, setColorFilters] = useState({
    green: true,
    orange: true,
    red: true,
    purple: true
  });


  const [expandedMahalle, setExpandedMahalle] = useState(new Set());
  const [expandedType, setExpandedType] = useState(new Set());
  const [expandedYama, setExpandedYama] = useState(new Set());
  const ALL = '__ALL__';

  const toggleOwnership = useCallback((key) => {
    setOwnershipSelection((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next.iskent && !next.belediye && !next.aksa && !next.hatsu && !next.enerjisa && !next.turktelekom && !next.superonline && !next.vodafone && !next.insaat) {
        next[key] = true;
      }
      return next;
    });
  }, []);

  const getTypesForMahalle = useCallback((m) => {
    const set = new Set(
      data
        .filter(d => d.Mahalle === m && passesOwnershipFilter(d, ownershipSelection))
        .map(d => (d.Type ?? 'GENEL'))
    );
    return Array.from(set).sort();
  }, [data, ownershipSelection]);

  const getYamaTypesForMahalleType = useCallback((m, t) => {
    const set = new Set(
      data
        .filter(d => d.Mahalle === m && (d.Type ?? 'GENEL') === t && passesOwnershipFilter(d, ownershipSelection))
        .map(d => (d.yamaType ?? 'GENEL'))
    );
    return Array.from(set).sort();
  }, [data, ownershipSelection]);

  const centerOnFiltered = useCallback((m, t = null, y = null) => {
    if (!mapInstanceRef.current) return;
    let items = data.filter(d => d.Mahalle === m && passesOwnershipFilter(d, ownershipSelection));
    if (t) items = items.filter(d => (d.Type ?? 'GENEL') === t);
    if (y) items = items.filter(d => (d.yamaType ?? 'GENEL') === y);
    if (!items.length) return;
    const bounds = L.latLngBounds(items.map(it => [it.Lat, it.Lng]));
    mapInstanceRef.current.fitBounds(bounds.pad(0.15));
  }, [data, ownershipSelection]);

  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [modalData, setModalData] = useState(null);
  const [stackList, setStackList] = useState(null);
  const [useMarkerSystem, setUseMarkerSystem] = useState(true);
  const [glifyLoaded, setGlifyLoaded] = useState(false);

  const glifyLockRef = useRef(false);
  // const glifyLocked = isMobile() && selectedNeighborhood === "__ALL__";
  const glifyLocked = false;

  useEffect(() => {
    glifyLockRef.current = glifyLocked;
    if (glifyLocked && useMarkerSystem) setUseMarkerSystem(false);
  }, [glifyLocked, useMarkerSystem]);

  useEffect(() => {
    const btn = document.querySelector('.pins-toggle .pins-btn');
    if (btn) {
      btn.disabled = glifyLocked;
      btn.setAttribute('aria-disabled', glifyLocked ? 'true' : 'false');
      btn.classList.toggle('is-disabled', glifyLocked);
    }
  }, [glifyLocked]);

  const isEmptyDate = (s) => {
    if (!s) return true;
    const str = String(s).trim().toLowerCase();
    if (!str) return true;
    if (str.startsWith('0001-01-01')) return true;
    if (str === '1/1/0001' || str === '01.01.0001' || str === '01.01.1 00:00') return true;

    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return true;
    if (d.getFullYear() <= 1) return true;
    return false;
  };

  const formatDateTime = useCallback((s) => {
    if (isEmptyDate(s)) return '';
    const d = new Date(s);
    return d.toLocaleString('tr-TR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  }, []);

  const nf = useMemo(() => new Intl.NumberFormat('tr-TR'), []);

  const tokens = useMemo(() => getSearchTokens(searchQuery), [searchQuery]);

  const filteredForTotals = useMemo(
    () => filterItemsForView(data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows),
    [data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows]
  );

  const filteredForTotalsMobile = useMemo(
    () => filterItemsForViewWithFallback(data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows),
    [data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows]
  );

  const totalsMobile = useMemo(() => {
    let fixed = 0, inProgress = 0, broken = 0, fromReport = 0;
    for (const it of filteredForTotalsMobile) {
      if (it.IsFixed === 4) fromReport++;
      else if (it.IsFixed === 2) fixed++;
      else if (it.IsFixed === 1) inProgress++;
      else broken++;
    }
    return { total: filteredForTotalsMobile.length, fixed, inProgress, broken, fromReport };
  }, [filteredForTotalsMobile]);

  const filteredForTotalsDesktop = useMemo(
    () => filterItemsForViewWithFallback(data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows),
    [data, tokens, selectedMahalles, colorFilters, ownershipSelection, insaatRows]
  );

  const totalsDesktop = useMemo(() => {
    let fixed = 0, inProgress = 0, broken = 0, fromReport = 0;
    for (const it of filteredForTotalsDesktop) {
      if (it.IsFixed === 4) fromReport++;
      else if (it.IsFixed === 2) fixed++;
      else if (it.IsFixed === 1) inProgress++;
      else broken++;
    }
    return { total: filteredForTotalsDesktop.length, fixed, inProgress, broken, fromReport };
  }, [filteredForTotalsDesktop]);


  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) setSidebarCollapsed(saved === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);


  const [lightboxSrc, setLightboxSrc] = useState(null);
  useEffect(() => {
    const img = document.querySelector('.pins-toggle .pins-img');
    const btn = document.querySelector('.pins-toggle .pins-btn');
    if (img) img.src = useMarkerSystem ? '/img/glify.png' : '/img/marker.png';
    if (btn) btn.title = useMarkerSystem ? 'Glify Pin' : 'Marker Pin';
  }, [useMarkerSystem]);

  useLayoutEffect(() => {
    const rootStyle = document.documentElement.style;
    const headerEl = document.querySelector('.site-header');
    const mobileEl = document.getElementById('mobileControls');

    const updateVars = () => {
      const headerH = Math.ceil(headerEl?.getBoundingClientRect().height || 56);
      rootStyle.setProperty('--site-header-h', headerH + 'px');

      const mobileH = Math.ceil(mobileEl?.getBoundingClientRect().height || 0);
      rootStyle.setProperty('--mobile-controls-h', mobileH + 'px');
    };

    updateVars();

    const roHeader = headerEl ? new ResizeObserver(updateVars) : null;
    const roMobile = mobileEl ? new ResizeObserver(updateVars) : null;
    roHeader?.observe(headerEl);
    roMobile?.observe(mobileEl);

    window.addEventListener('resize', updateVars);
    return () => {
      roHeader?.disconnect();
      roMobile?.disconnect();
      window.removeEventListener('resize', updateVars);
    };
  }, []);



  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const allPinsLayerRef = useRef(null);
  const glifyLayerRef = useRef(null);
  const markersRef = useRef([]);
  const baseLayersRef = useRef({ street: null, satellite: null });
  const activeBaseRef = useRef('street');
  const clusterGroupsRef = useRef({ red: null, orange: null, green: null, purple: null });
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const submitAbortRef = useRef(null);
  const reportMapContainerRef = useRef(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({
    name: '',
    mahalle: '',
    imageFile: null,
    lat: null,
    lng: null,
  });

  const reportMapRef = useRef(null);
  const reportMarkerRef = useRef(null);
  const placeMarkerRef = useRef(null);

  const mahalleList = useMemo(
    () => Array.from(allMahallesSet).sort(),
    [allMahallesSet]
  );

  // Manage browser history for back button support (History Trap)
  useEffect(() => {
    // Initial trap on mount
    window.history.pushState(null, null, window.location.href);
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      // Re-apply trap to prevent route change
      window.history.pushState(null, null, window.location.href);

      if (lightboxSrc) {
        setLightboxSrc(null);
        return;
      }
      if (stackList) {
        setStackList(null);
        return;
      }
      if (modalData) {
        setModalData(null);
        return;
      }
      if (reportOpen) {
        setReportOpen(false);
        return;
      }

      // View return to list (neighborhood selection)
      if (selectedMahalles.size > 0 && !showAllPins) {
        setSelectedMahalles(new Set());
        return;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [lightboxSrc, stackList, modalData, reportOpen, selectedMahalles, showAllPins]);

  useEffect(() => {
    if (!(modalData || stackList || lightboxSrc || reportOpen)) return;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      if (lightboxSrc) {
        setLightboxSrc(null);
        return;
      }

      if (stackList) {
        setStackList(null);
        return;
      }

      if (modalData) {
        setModalData(null);
        return;
      }

      if (reportOpen) {
        setReportOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalData, stackList, lightboxSrc, reportOpen]);

  useEffect(() => {
    if (!reportOpen) {
      if (reportMapRef.current) {
        reportMapRef.current.remove();
        reportMapRef.current = null;
      }
      reportMarkerRef.current = null;
      placeMarkerRef.current = null;
      return;
    }

    const el = reportMapContainerRef.current;
    if (!el) return;

    if (el._leaflet_id) {
      try { el._leaflet_id = null; } catch { }
    }

    const start =
      (mapInstanceRef.current?.getCenter && mapInstanceRef.current.getCenter()) ||
      L.latLng(36.4, 36.2);

    const m = L.map(el, { center: start, zoom: 13 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(m);

    const placeMarker = (latlng) => {
      if (reportMarkerRef.current) {
        m.removeLayer(reportMarkerRef.current);
        reportMarkerRef.current = null;
      }
      const mk = L.marker(latlng, { draggable: true }).addTo(m);
      reportMarkerRef.current = mk;

      setReportForm((f) => ({ ...f, lat: latlng.lat, lng: latlng.lng }));

      mk.on('moveend', (e) => {
        const p = e.target.getLatLng();
        setReportForm((f) => ({ ...f, lat: p.lat, lng: p.lng }));
      });
    };

    m.on('click', (ev) => {
      placeMarker(ev.latlng);
    });

    reportMapRef.current = m;
    placeMarkerRef.current = placeMarker;

    return () => {
      m.remove();
      reportMapRef.current = null;
      reportMarkerRef.current = null;
      placeMarkerRef.current = null;
    };
  }, [reportOpen]);



  const SLOW_MARKER_MS = 6000;

  const [autoSwitched, setAutoSwitched] = useState(false);
  const alreadyAutoSwitchedRef = useRef(false);
  useEffect(() => { alreadyAutoSwitchedRef.current = false; }, [
    showAllPins, searchQuery, selectedMahalles, colorFilters
  ]);

  useEffect(() => {
    if (!autoSwitched) return;
    const t = setTimeout(() => setAutoSwitched(false), 3000);
    return () => clearTimeout(t);
  }, [autoSwitched]);

  const centerOnMahalle = useCallback((mahalle) => {
    if (!mapInstanceRef.current || !mahalle) return;
    const items = data.filter(d => d.Mahalle === mahalle && passesOwnershipFilter(d, ownershipSelection));
    if (!items.length) return;
    const bounds = L.latLngBounds(items.map(it => [it.Lat, it.Lng]));
    mapInstanceRef.current.fitBounds(bounds.pad(0.15));
  }, [data, ownershipSelection]);


  const centerOnAll = useCallback(() => {
    if (!mapInstanceRef.current) return;

    const baseData = selectedMahalles.size > 0
      ? data.filter(it => selectedMahalles.has(it.Mahalle))
      : data;
    const targetData = baseData.filter(it => passesOwnershipFilter(it, ownershipSelection));

    if (targetData.length === 0) return;

    const bounds = L.latLngBounds(targetData.map(it => [it.Lat, it.Lng]));
    const center = bounds.getCenter();
    mapInstanceRef.current.setView([center.lat - 0.05, center.lng], 13);
  }, [data, selectedMahalles, ownershipSelection]);

  useEffect(() => {
    loadGlifyScript().then(() => setGlifyLoaded(true));
  }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    let cancelled = false;
    const map = L.map(mapRef.current, { preferCanvas: true }).setView([36.4, 36.2], 12);
    map.createPane('pane-red');
    map.createPane('pane-orange');
    map.createPane('pane-green');
    map.createPane('pane-purple');

    map.getPane('pane-red').style.zIndex = 630;
    map.getPane('pane-orange').style.zIndex = 640;
    map.getPane('pane-green').style.zIndex = 650;
    map.getPane('pane-purple').style.zIndex = 660;

    map.getPane('pane-red').classList.add('pane-red');
    map.getPane('pane-orange').classList.add('pane-orange');
    map.getPane('pane-green').classList.add('pane-green');
    map.getPane('pane-purple').classList.add('pane-purple');

    const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 21,
      maxNativeZoom: 19,
      tileSize: 256,
      zoomOffset: 0
    });


    const satellite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, GIS User Community',
        maxZoom: 21,
        maxNativeZoom: 18,
        tileSize: 256,
        zoomOffset: 0
      }
    );


    baseLayersRef.current = { street, satellite };
    satellite.addTo(map);
    activeBaseRef.current = 'satellite';

    function setBasemap(type) {
      const base = baseLayersRef.current;
      if (!base[type]) return;
      if (activeBaseRef.current && base[activeBaseRef.current]) {
        map.removeLayer(base[activeBaseRef.current]);
      }
      base[type].addTo(map);
      activeBaseRef.current = type;

      const img = document.querySelector('.layers-toggle .layers-img');
      if (img) img.src = LAYER_ICON[type];
      const btn = document.querySelector('.layers-toggle .layers-btn');
      if (btn) {
        btn.setAttribute('aria-label', type === 'street' ? 'Sokak haritasına geç' : 'Uydu haritasına geç');
        btn.title = type === 'street' ? 'Sokak' : 'Uydu';
      }
    }

    const LayerToggleControl = L.Control.extend({
      onAdd() {
        const container = L.DomUtil.create('div', 'layers-toggle');

        const isSat = activeBaseRef.current === 'satellite';
        container.innerHTML = `
          <button class="layers-btn"
                  title="${isSat ? 'Sokak' : 'Uydu'}"
                  aria-label="${isSat ? 'Sokak haritasına geç' : 'Uydu haritasına geç'}">
            <img class="layers-img" alt="Layer" draggable="false"
                 src="${LAYER_ICON[activeBaseRef.current]}"/>
          </button>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        const btn = container.querySelector('.layers-btn');
        btn.addEventListener('click', () => {
          const next = activeBaseRef.current === 'street' ? 'satellite' : 'street';
          setBasemap(next);
        });

        return container;
      }
    });
    map.addControl(new LayerToggleControl({ position: 'topright' }));
    const PinsToggleControl = L.Control.extend({
      onAdd() {
        const container = L.DomUtil.create('div', 'pins-toggle');
        container.innerHTML = `
          <button class="pins-btn layers-btn" title="${useMarkerSystem ? 'Marker Pin' : 'Glify Pin'}" aria-label="Pin modu">
            <img class="pins-img layers-img" alt="Pins" draggable="false" src="${useMarkerSystem ? '/img/glify.png' : '/img/marker.png'}"/>
          </button>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        const btn = container.querySelector('.pins-btn');
        btn.addEventListener('click', () => {
          if (glifyLockRef.current) return;
          setUseMarkerSystem(prev => !prev);
        });


        return container;
      }
    });
    map.addControl(new PinsToggleControl({ position: 'topright' }));

    const ReportButtonControl = L.Control.extend({
      onAdd() {
        const container = L.DomUtil.create('div', 'report-toggle');
        container.innerHTML = `
          <button class="icon-square report-btn"
                  title="Arıza bildir"
                  aria-label="Arıza bildir">
            <i class="fas fa-bell" aria-hidden="true"></i>
          </button>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        const btn = container.querySelector('.report-btn');
        btn.addEventListener('click', () => {
          setReportOpen(true);
        });

        return container;
      }
    });
    map.addControl(new ReportButtonControl({ position: 'topright' }));

    const OwnershipToggleControl = L.Control.extend({
      onAdd() {
        const container = L.DomUtil.create('div', 'ownership-toggle-control mobile-only');
        container.style.display = 'flex';
        container.style.gap = '8px';
        container.style.flexDirection = 'column';
        container.style.alignItems = 'flex-end';

        const belediyeBtn = L.DomUtil.create('button', 'ownership-toggle-mobile');
        belediyeBtn.innerHTML = `<img src="/img/isklogo.png" alt="İskenderun Belediyesi" style="height: 28px; width: auto; max-width: 100%; display: block; object-fit: contain;" />`;
        belediyeBtn.title = 'İskenderun Belediyesi';

        const iskentBtn = L.DomUtil.create('button', 'ownership-toggle-mobile');
        iskentBtn.innerHTML = `<img src="/img/iskentlogo_min.png" alt="Iskent" style="height: 28px; width: auto; max-width: 100%; display: block; object-fit: contain;" />`;
        iskentBtn.title = 'Iskent';

        const insaatBtn = L.DomUtil.create('button', 'ownership-toggle-mobile');
        insaatBtn.innerHTML = `<i class="fas fa-hard-hat" style="font-size: 20px; color: ${ownershipSelectionRef.current.insaat ? 'white' : '#d97706'}"></i>`;
        insaatBtn.title = 'İnşaat Noktaları';

        const updateButtons = () => {
          const current = ownershipSelectionRef.current;
          belediyeBtn.style.background = current.belediye ? '#22a6ff' : '#ffffff';
          belediyeBtn.style.borderColor = current.belediye ? '#22a6ff' : '#dbe1ea';
          iskentBtn.style.background = current.iskent ? '#22a6ff' : '#ffffff';
          iskentBtn.style.borderColor = current.iskent ? '#22a6ff' : '#dbe1ea';
          insaatBtn.style.background = current.insaat ? '#d97706' : '#ffffff';
          insaatBtn.style.borderColor = current.insaat ? '#d97706' : '#dbe1ea';
          const icon = insaatBtn.querySelector('i');
          if (icon) icon.style.color = current.insaat ? 'white' : '#d97706';
        };

        updateButtons();

        belediyeBtn.addEventListener('click', () => {
          toggleOwnership('belediye');
          setTimeout(updateButtons, 50);
        });

        iskentBtn.addEventListener('click', () => {
          toggleOwnership('iskent');
          setTimeout(updateButtons, 50);
        });

        insaatBtn.addEventListener('click', () => {
          toggleOwnership('insaat');
          setTimeout(updateButtons, 50);
        });

        container.appendChild(belediyeBtn);
        container.appendChild(iskentBtn);
        container.appendChild(insaatBtn);

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        const intervalId = setInterval(() => {
          updateButtons();
        }, 200);

        container.addEventListener('remove', () => {
          clearInterval(intervalId);
        });

        return container;
      }
    });
    map.addControl(new OwnershipToggleControl({ position: 'topright' }));

    map.whenReady(() => {
      let cancelled = false;
      setTimeout(() => {
        if (!cancelled && map._loaded && map._size && map.getContainer()) {
          map.invalidateSize();
        }
      }, 0);
    });
    // L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    //   attribution: '© OpenStreetMap contributors'
    // }).addTo(map);

    mapInstanceRef.current = map;
    const commonClusterOpts = {
      maxClusterRadius: (z) => (z >= 17 ? 32 : 8),
      spiderfyOnClick: true,
      zoomToBoundsOnClick: true,
      showCoverageOnHover: false,
      spiderLegPolylineOptions: { weight: 1, opacity: 0.6 },
    };

    const greenAlmostNoCluster = {
      ...commonClusterOpts,
      maxClusterRadius: 10,
      chunkedLoading: true,
    };

    const makeIconDiv = (img, extraClass = '') => L.divIcon({
      html: `<div class="custom-cluster">
              <img src="${img}" class="cluster-pin" />
            </div>`,
      className: `marker-cluster ${extraClass}`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    clusterGroupsRef.current.purple = L.markerClusterGroup({
      ...commonClusterOpts,
      pane: 'pane-purple',
      iconCreateFunction: () => makeIconDiv('/img/purple-pin.png', 'cluster-purple')
    });

    clusterGroupsRef.current.red = L.markerClusterGroup({
      ...commonClusterOpts,
      pane: 'pane-red',
      iconCreateFunction: () => makeIconDiv('/img/red-pin.png', 'cluster-red')
    });
    clusterGroupsRef.current.orange = L.markerClusterGroup({
      ...commonClusterOpts,
      pane: 'pane-orange',
      iconCreateFunction: () => makeIconDiv('/img/orange-pin.png', 'cluster-orange')
    });

    clusterGroupsRef.current.insaat = L.markerClusterGroup({
      ...commonClusterOpts,
      iconCreateFunction: () => L.divIcon({
        html: `<div class="custom-cluster cluster-insaat" style="background-color: #d97706; border-color: #d97706;">
                 <i class="fas fa-hard-hat" style="color: white; font-size: 16px;"></i>
               </div>`,
        className: 'marker-cluster cluster-insaat',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      })
    });

    clusterGroupsRef.current.green = L.markerClusterGroup({
      ...greenAlmostNoCluster,
      pane: 'pane-green',
      iconCreateFunction: () => makeIconDiv('/img/green-pin.png', 'cluster-green')
    });



    map.addLayer(clusterGroupsRef.current.red);
    map.addLayer(clusterGroupsRef.current.orange);
    map.addLayer(clusterGroupsRef.current.green);
    map.addLayer(clusterGroupsRef.current.purple);
    map.addLayer(clusterGroupsRef.current.insaat);

    ['red', 'orange', 'purple'].forEach((key) => {
      clusterGroupsRef.current[key].on('clusterclick', (a) => a.layer.spiderfy());
    });
    clusterGroupsRef.current.green.on('clusterclick', (a) => a.layer.spiderfy());

    // allPinsLayerRef.current = L.markerClusterGroup({
    //   maxClusterRadius: (z) => (z >= 17 ? 32 : 4),
    //   spiderfyOnClick: true,
    //   zoomToBoundsOnClick: true, 
    //   showCoverageOnHover: false,
    //   spiderLegPolylineOptions: { weight: 1, opacity: 0.6 },
    //   iconCreateFunction: () => {
    //     return L.divIcon({
    //       html: `
    //         <div class="custom-cluster">
    //           <img src="/img/red-pin.png" class="cluster-pin" />
    //         </div>
    //       `,
    //       className: '',
    //       iconSize: [32, 32],
    //       iconAnchor: [16, 32],
    //     });
    //   }
    // });


    // mapInstanceRef.current.addLayer(allPinsLayerRef.current);
    // allPinsLayerRef.current.on('clusterclick', function (a) {
    //   a.layer.spiderfy();
    // });

    return () => {
      cancelled = true;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.stop();
      }

      if (glifyLayerRef.current) {
        try {
          if (mapInstanceRef.current && mapInstanceRef.current.hasLayer && mapInstanceRef.current.hasLayer(glifyLayerRef.current)) {
            mapInstanceRef.current.removeLayer(glifyLayerRef.current);
          }
          glifyLayerRef.current.remove();
        } catch (error) {
          console.warn('Error removing glify layer:', error);
        }
        glifyLayerRef.current = null;
      }

      if (clusterGroupsRef.current?.red) {
        const groups = clusterGroupsRef.current;
        Object.values(groups).forEach(group => {
          try {
            if (group && group.clearLayers) {
              group.clearLayers();
            }
            if (mapInstanceRef.current && mapInstanceRef.current.hasLayer && mapInstanceRef.current.hasLayer(group)) {
              mapInstanceRef.current.removeLayer(group);
            }
          } catch (error) {
            console.warn('Error cleaning up cluster group:', error);
          }
        });
      }

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };

  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !mapRef.current) return;

    const ro = new ResizeObserver(() => {
      const m = mapInstanceRef.current;
      const c = m?.getContainer?.();
      if (m && c && c.parentNode) {
        m.invalidateSize({ animate: false });
      }
    });

    if (mapRef.current?.isConnected) {
      ro.observe(mapRef.current);
    }

    const onWinResize = () => {
      const m = mapInstanceRef.current;
      const c = m?.getContainer?.();
      if (m && c && c.parentNode) {
        m.invalidateSize();
      }
    };

    window.addEventListener('resize', onWinResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onWinResize);
    };
  }, []);

  useEffect(() => {
    if (showAllPins && selectedMahalles.size > 0) {
      centerOnAll();
    }
  }, [showAllPins, selectedMahalles, centerOnAll]);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_BASE}/api/faults`)
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(list => {
        const normalized = (list || []).filter((x) => !isHiddenFaultOwnershipType(x)).map((x) => {
          const createdByRaw = x.createdByUserId ?? x.createdBy ?? x.CreatedByUserId ?? x.CreatedBy ?? x.userId ?? x.UserId ?? null;
          const createdById = createdByRaw ? String(createdByRaw).trim().toLowerCase() : null;
          const ownershipFromApi = x.createdByUserOwnershipType ?? x.CreatedByUserOwnershipType ?? null;
          const normalizedOwnership = createdById ? (ownershipFromApi ?? 'Belediye') : 'Belediye';
          return {
            ID: x.id ?? x.Id,
            Mahalle: x.mahalle ?? x.Mahalle,
            Type: x.type ?? x.Type,
            yamaType: x.yamaType,
            Lat: +(x.lat ?? x.Lat),
            Lng: +(x.lng ?? x.Lng),
            ImagePath: x.imagePath ?? x.ImagePath ?? null,
            IsFixed: +(x.isFixed ?? x.IsFixed ?? 0),
            StartDate: x.startDate ?? x.StartDate ?? null,
            FinishDate: x.finishDate ?? x.FinishDate ?? null,
            Description: x.description ?? x.Description ?? null,
            CreatedById: createdById,
            CreatedByUserOwnershipType: normalizedOwnership,
            CollaborationCompanyIds: x.collaborationCompanyIds ?? x.CollaborationCompanyIds ?? null,
          };
        });
        setData(normalized);
      })
      .catch(err => console.error('Faults API hatası:', err))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!ownershipSelection.insaat) {
      setInsaatRows([]);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/faults/Insaat`)
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(list => {
        if (!cancelled) {
          setInsaatRows((list || []).filter((x) => !isHiddenFaultOwnershipType(x)).map(x => ({
            ID: x.id ?? x.Id,
            Mahalle: x.mahalle ?? x.Mahalle,
            Type: x.type ?? x.Type,
            Lat: +(x.lat ?? x.Lat),
            Lng: +(x.lng ?? x.Lng),
            IsInsaat: true,
            isFixed: 0,
            Description: x.description ?? x.Description ?? null,
            ImagePath: x.imagePath ?? x.ImagePath ?? null,
          })));
        }
      })
      .catch(err => console.error('Insaat API hatası:', err));
    return () => { cancelled = true; };
  }, [ownershipSelection.insaat]);

  const buildPopup = (item) => {
    const statusText = item.IsFixed === 4 ? 'Bildirimden Gelen' :
      item.IsFixed === 2 ? 'Onarıldı' :
        item.IsFixed === 1 ? 'Onarılıyor' :
          'Arızalı';
    const color = item.IsFixed === 4 ? 'purple' :
      item.IsFixed === 2 ? 'green' :
        item.IsFixed === 1 ? 'orange' :
          'red';

    return `
      <div style="text-align:center;min-width:140px;font-family:system-ui,sans-serif;">
        <strong style="font-size:14px;line-height:1.35;">
          #${item.ID}<br>
          ${item.Mahalle}
        </strong><br>
        <span style="color: ${color}; font-weight: bold;">${statusText}</span><br>
        
        <button onclick="window.showDetails(${item.ID})"
                style="margin-top:12px;padding:8px 16px;font-size:14px;
                      border:none;border-radius:6px;background:#434343;
                      color:#fff;cursor:pointer;">
          Detayları Gör
        </button>
      </div>
    `;
  };

  const createMarkersForItem = (item) => {
    let icon;
    if (item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat') {
      icon = insaatPinIcon;
    } else if (item.IsFixed === 4) {
      icon = purplePinIcon;
    } else if (item.IsFixed === 2) {
      icon = greenPinIcon;
    } else if (item.IsFixed === 1) {
      icon = orangePinIcon;
    } else {
      icon = redPinIcon;
    }

    const marker = L.marker([+item.Lat, +item.Lng], { icon });
    marker.on('click', () => {
      setModalData(item);
    });

    return { marker, item };
  };

  const convertToGeoJSON = (data) => {
    return {
      type: "FeatureCollection",
      features: data.map(item => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [item.Lng, item.Lat]
        },
        properties: item
      }))
    };
  };

  const handleUseDeviceLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Cihaz konumu desteklenmiyor.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = L.latLng(pos.coords.latitude, pos.coords.longitude);
        if (reportMapRef.current && placeMarkerRef.current) {
          reportMapRef.current.setView(latlng, 17);
          placeMarkerRef.current(latlng);
        } else {
          setReportForm((f) => ({ ...f, lat: latlng.lat, lng: latlng.lng }));
        }
      },
      (err) => toast.error(`Konum alınamadı: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();

    if (submitLockRef.current || submitting) return;
    submitLockRef.current = true;
    setSubmitting(true);

    try {
      if (!reportForm.name || !reportForm.mahalle || !reportForm.lat || !reportForm.lng) {
        toast('Lütfen isim, mahalle ve konumu doldurun.', { icon: '📝' });
        return;
      }

      const fd = new FormData();
      fd.append('ReporterName', reportForm.name);
      fd.append('Mahalle', reportForm.mahalle);
      fd.append('Lat', String(reportForm.lat));
      fd.append('Lng', String(reportForm.lng));
      if (reportForm.type) fd.append('Type', reportForm.type);
      if (reportForm.yamaType) fd.append('yamaType', reportForm.yamaType);
      if (reportForm.note) fd.append('Description', reportForm.note);
      if (reportForm.imageFile) fd.append('image', reportForm.imageFile, reportForm.imageFile.name);

      const ac = new AbortController();
      submitAbortRef.current = ac;

      const task = (async () => {
        const resp = await fetch(`${API_BASE}/api/FaultReport`, {
          method: 'POST',
          body: fd,
          signal: ac.signal
        });

        const raw = await resp.text();
        let data; try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }

        if (!resp.ok) throw new Error(data?.Message || data?.Error || raw || `HTTP ${resp.status}`);
        return data || {};
      })();

      const res = await toast.promise(task, {
        loading: 'Gönderiliyor…',
        success: (r) => (r.imageSaved ?? r.ImageSaved)
          ? 'Bildiriminiz kaydedildi. Görsel başarıyla yüklendi.'
          : 'Bildiriminiz kaydedildi. Görsel yüklenemedi.',
        error: (err) => `Gönderilemedi: ${err.message ?? err}`
      });

      console.log('API Response:', res);
      if (submitAbortRef.current) submitAbortRef.current.abort();
      setReportOpen(false);
      setReportForm({ name: '', mahalle: '', type: '', yamaType: '', note: '', imageFile: null, lat: null, lng: null });
    } catch (err) {
      console.error('FaultReport API hatası:', err);
    } finally {
      if (submitAbortRef.current) submitAbortRef.current = null;
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const openPopupWithHandler = (latlng, item) => {
    const popup = L.popup({ offset: [0, -12] })
      .setLatLng(latlng)
      .setContent(buildPopup(item));

    popup.once('add', () => {
      const btn = popup.getElement()?.querySelector('button');
      if (btn) btn.addEventListener('click', () => { setModalData(item); });
    });

    popup.openOn(mapInstanceRef.current);
  };

  const repaintMarkers = useCallback(async () => {
    if (!mapInstanceRef.current) return;

    const tokens = getSearchTokens(searchQuery);
    const hasQuery = tokens.length > 0;

    const filteredData = [...data, ...insaatRows].filter(item => {
      const matchesSearch = matchItemByTokens(item, tokens);

      const inMahalle = selectedMahalles.size > 0 && selectedMahalles.has(item.Mahalle);

      const typeOk = !selectedType || ((item.Type ?? 'GENEL') === selectedType);
      const yamaOk = !selectedYamaType || ((item.yamaType ?? 'GENEL') === selectedYamaType);
      const ownershipOk = passesOwnershipFilter(item, ownershipSelection);

      let shouldShow = hasQuery ? (matchesSearch && inMahalle) : inMahalle;
      const isSpecial = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;

      if (isSpecial) return shouldShow && ownershipOk;

      const color = item.IsFixed === 4 ? 'purple'
        : item.IsFixed === 2 ? 'green'
          : item.IsFixed === 1 ? 'orange'
            : 'red';
      const passes = colorFilters[color];

      return shouldShow && typeOk && yamaOk && passes && ownershipOk;
    });


    if (useMarkerSystem) {
      if (glifyLayerRef.current) {
        glifyLayerRef.current.remove();
        glifyLayerRef.current = null;
      }

      const groups = clusterGroupsRef.current;
      if (!groups.red) return;

      groups.red.clearLayers();
      groups.orange.clearLayers();
      groups.green.clearLayers();
      groups.purple.clearLayers();
      groups.insaat.clearLayers();

      const batch = { red: [], orange: [], green: [], purple: [], insaat: [] };

      const t0 = performance.now();
      let tooSlow = false;
      const BATCH_SIZE = 200;

      for (let i = 0; i < filteredData.length; i++) {
        const item = filteredData[i];

        let icon = redPinIcon;
        let bucket = 'red';
        if (item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat) {
          icon = insaatPinIcon;
          bucket = 'insaat';
        } else if (item.IsFixed === 1) { icon = orangePinIcon; bucket = 'orange'; }
        else if (item.IsFixed === 2) { icon = greenPinIcon; bucket = 'green'; }
        else if (item.IsFixed === 4) { icon = purplePinIcon; bucket = 'purple'; }

        const zIndexOffset = (item.IsInsaat ? 400 : item.IsFixed === 4 ? 300 : item.IsFixed === 2 ? 200 : item.IsFixed === 1 ? 100 : 0);
        const pane = (bucket === 'purple') ? 'pane-purple' : (bucket === 'green') ? 'pane-green' : (bucket === 'orange' ? 'pane-orange' : 'pane-red');

        const m = L.marker([+item.Lat, +item.Lng], { icon, zIndexOffset, pane });

        m.on('click', () => { setModalData(item); });
        batch[bucket].push(m);

        if (i > 0 && i % BATCH_SIZE === 0) {
          await sleep(0);
          if (showAllPins) {
            const elapsed = performance.now() - t0;
            if (elapsed > SLOW_MARKER_MS && !alreadyAutoSwitchedRef.current) {
              tooSlow = true;
              break;
            }
          }
        }
      }

      if (tooSlow && !alreadyAutoSwitchedRef.current) {
        alreadyAutoSwitchedRef.current = true;
        const swap = () => { setUseMarkerSystem(false); setAutoSwitched(true); };
        if (!glifyLoaded) { loadGlifyScript().then(swap); } else { swap(); }
        return;
      }

      if (batch.red.length) groups.red.addLayers(batch.red);
      if (batch.orange.length) groups.orange.addLayers(batch.orange);
      if (batch.green.length) groups.green.addLayers(batch.green);
      if (batch.purple.length) groups.purple.addLayers(batch.purple);
      if (batch.insaat.length) groups.insaat.addLayers(batch.insaat);

      const map = mapInstanceRef.current;

      [groups.red, groups.orange, groups.green, groups.purple, groups.insaat].forEach(g => {
        if (map.hasLayer(g)) map.removeLayer(g);
      });

      if (colorFilters.red) map.addLayer(groups.red);
      if (colorFilters.orange) map.addLayer(groups.orange);
      if (colorFilters.green) map.addLayer(groups.green);
      if (colorFilters.purple) map.addLayer(groups.purple);
      if (ownershipSelection.insaat) map.addLayer(groups.insaat);

      return;
    } else {
      (function () {
        const map = mapInstanceRef.current;
        const groups = clusterGroupsRef.current;
        if (map && groups.red) {
          Object.values(groups).forEach(g => {
            try {
              g.clearLayers();
            } catch { }
            if (map.hasLayer(g)) map.removeLayer(g);
          });
        }
      })();

      if (!glifyLoaded || !window.L?.glify) return;

      // if (allPinsLayerRef.current) {
      //   allPinsLayerRef.current.clearLayers();
      // }
      if (glifyLayerRef.current) {
        glifyLayerRef.current.remove();
      }

      const ordered = [
        ...filteredData.filter(it => it.IsFixed !== 1 && it.IsFixed !== 2 && it.IsFixed !== 4),
        ...filteredData.filter(it => it.IsFixed === 1),
        ...filteredData.filter(it => it.IsFixed === 2),
        ...filteredData.filter(it => it.IsFixed === 4),
      ];
      const geojsonData = convertToGeoJSON(ordered);


      glifyLayerRef.current = window.L.glify.points({
        map: mapInstanceRef.current,
        data: geojsonData,
        size: 10,
        color: function (_index, feature) {
          const it = feature.properties;
          if (it.IsFixed === 4) return { r: 0.5, g: 0, b: 1 };
          if (it.IsFixed === 2) return { r: 0, g: 1, b: 0 };
          if (it.IsFixed === 1) return { r: 1, g: 0.5, b: 0 };
          return { r: 1, b: 0, g: 0 };
        },
        opacity: 1,
        click: (_e, feature) => {
          const item = feature.properties;
          const EPS = 1e-7;
          const sameSpot = data.filter(d =>
            Math.abs(+d.Lat - +item.Lat) < EPS && Math.abs(+d.Lng - +item.Lng) < EPS
          );

          if (sameSpot.length > 1) {
            setStackList(sameSpot);
          } else {
            setModalData(item);
          }
        }

      });

      glifyLayerRef.current.addTo(mapInstanceRef.current);
    }
  }, [data, insaatRows, searchQuery, showAllPins, selectedMahalles, selectedType, selectedYamaType, colorFilters, ownershipSelection, useMarkerSystem, glifyLoaded]);

  useEffect(() => {
    repaintMarkers();
  }, [repaintMarkers]);

  useEffect(() => {
    if (useMarkerSystem) {
      alreadyAutoSwitchedRef.current = false;
    }
  }, [useMarkerSystem]);

  useEffect(() => {
    window.showDetails = (id) => {
      const item = data.find(d => d.ID === id);
      if (item) setModalData(item);
    };
  }, [data]);

  // useEffect(() => {
  //   if (selectedId && mapInstanceRef.current) {
  //     const item = data.find(d => d.ID == selectedId);
  //     if (item) {
  //       mapInstanceRef.current.setView([item.Lat, item.Lng], 17);

  //       if (useMarkerSystem) {
  //         const markerData = markersRef.current.find(m => m.item.ID == item.ID);
  //         if (markerData && allPinsLayerRef.current.hasLayer(markerData.marker)) {
  //           markerData.marker.openPopup();
  //         } else {
  //           // Create temporary marker for mobile
  //           if (mobileTempMarkerRef.current) {
  //             mapInstanceRef.current.removeLayer(mobileTempMarkerRef.current);
  //           }

  //           const icon = item.IsFixed === 2 ? greenPinIcon :
  //                       item.IsFixed === 1 ? orangePinIcon : redPinIcon;

  //           mobileTempMarkerRef.current = L.marker([item.Lat, item.Lng], { icon })
  //             .addTo(mapInstanceRef.current)
  //             .bindPopup(buildPopup(item))
  //             .openPopup();
  //         }
  //       } else {
  //         openPopupWithHandler(L.latLng(item.Lat, item.Lng), item);
  //       }
  //     }
  //   }
  // }, [selectedId, data, useMarkerSystem]);

  const renderSidebar = () => {
    const tokens = getSearchTokens(searchQuery);
    const filteredData = [...data, ...insaatRows]
      .filter(item => matchItemByTokens(item, tokens))
      .filter(item => passesOwnershipFilter(item, ownershipSelection))
      .filter(item => {
        const isSpecial = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;
        return isSpecial || passesColorFilter(item, colorFilters);
      });
    if (isLoading) {
      return Array.from({ length: 6 }).map((_, i) => (
        <div key={`sk-${i}`} className="mahalle-group">
          <div className="accordion-header">
            <span className="skeleton skeleton-text" style={{ width: '60%' }} />
            <div className="fault-counts" style={{ marginLeft: 'auto' }}>
              <span className="skeleton skeleton-dot" />
              <span className="skeleton skeleton-dot" />
              <span className="skeleton skeleton-dot" />
            </div>
          </div>
          <div className="mahalle-divider" role="separator" aria-hidden="true" />
        </div>
      ));
    }
    const tree = {};
    for (const item of filteredData) {
      const m = item.Mahalle;
      const t = (item.Type ?? 'GENEL');
      const y = (item.yamaType ?? 'GENEL');

      tree[m] ??= { counts: { red: 0, orange: 0, green: 0, purple: 0, insaat: 0 }, types: {} };
      const isSpecial = item.IsInsaat || item.type === 'Insaat' || item.Type === 'Insaat' || item.isInsaat;
      const colorKey = isSpecial ? 'insaat' :
        item.IsFixed === 4 ? 'purple' :
          item.IsFixed === 2 ? 'green' :
            item.IsFixed === 1 ? 'orange' : 'red';
      tree[m].counts[colorKey]++;

      tree[m].types[t] ??= { counts: { red: 0, orange: 0, green: 0, purple: 0, insaat: 0 }, yamas: {} };
      tree[m].types[t].counts[colorKey]++;

      tree[m].types[t].yamas[y] ??= { counts: { red: 0, orange: 0, green: 0, purple: 0, insaat: 0 }, items: [] };
      tree[m].types[t].yamas[y].counts[colorKey]++;
      tree[m].types[t].yamas[y].items.push(item);
    }

    const makeBadge = (n, cls) => n > 0 ? <span className={`count-badge ${cls}`}>{n}</span> : null;

    return Object.keys(tree).sort().map(mahalle => {
      const mNode = tree[mahalle];
      const mKeyOpen = expandedMahalle.has(mahalle);
      const isActive = selectedMahalles.has(mahalle);

      return (
        <div key={mahalle} className="mahalle-group">
          <div
            className={`accordion-header ${isActive ? 'active' : ''}`}
            onClick={() => {
              setSelectedMahalles(new Set([mahalle]));
              setSelectedType(null);
              setSelectedYamaType(null);
              setShowAllPins(false);
              centerOnMahalle(mahalle);
              const next = new Set(expandedMahalle);
              if (next.has(mahalle)) next.delete(mahalle); else next.add(mahalle);
              setExpandedMahalle(next);
            }}
          >
            <span>{mahalle}</span>
            <div className="fault-counts">
              {makeBadge(mNode.counts.red, 'red')}
              {makeBadge(mNode.counts.orange, 'orange')}
              {makeBadge(mNode.counts.green, 'green')}
              {makeBadge(mNode.counts.purple, 'purple')}
              {mNode.counts.insaat > 0 && <span className="count-badge insaat"><i className="fas fa-hard-hat"></i> {mNode.counts.insaat}</span>}
            </div>
          </div>

          {mKeyOpen && Object.keys(mNode.types).sort().map(typeName => {
            const tNode = mNode.types[typeName];
            const tKey = `${mahalle}||${typeName}`;
            const tOpen = expandedType.has(tKey);
            return (
              <div key={tKey}>
                <div
                  className="subgroup-header"
                  onClick={() => {
                    setSelectedMahalles(new Set([mahalle]));
                    setSelectedType(typeName);
                    setSelectedYamaType(null);
                    centerOnFiltered(mahalle, typeName, null);
                    const next = new Set(expandedType);
                    if (next.has(tKey)) next.delete(tKey); else next.add(tKey);
                    setExpandedType(next);
                  }}
                  title={`${mahalle} / ${getTypeDisplayName(typeName)}`}
                >
                  <i className="fas fa-folder" aria-hidden="true" />
                  <strong>{getTypeDisplayName(typeName)}</strong>
                  <div className="fault-counts" style={{ marginLeft: 'auto' }}>
                    {makeBadge(tNode.counts.red, 'red')}
                    {makeBadge(tNode.counts.orange, 'orange')}
                    {makeBadge(tNode.counts.green, 'green')}
                    {makeBadge(tNode.counts.purple, 'purple')}
                  </div>
                </div>

                {tOpen && Object.keys(tNode.yamas).sort().map(yName => {
                  const yNode = tNode.yamas[yName];
                  const yKey = `${tKey}||${yName}`;
                  const yOpen = expandedYama.has(yKey);

                  return (
                    <div key={yKey}>
                      <div
                        className="subgroup-header level-2"
                        onClick={() => {
                          setSelectedMahalles(new Set([mahalle]));
                          setSelectedType(typeName);
                          setSelectedYamaType(yName);
                          centerOnFiltered(mahalle, typeName, yName);

                          const next = new Set(expandedYama);
                          if (next.has(yKey)) next.delete(yKey); else next.add(yKey);
                          setExpandedYama(next);
                        }}
                        title={`${mahalle} / ${getTypeDisplayName(typeName)} / ${yName}`}
                      >
                        <i className={`fas ${yOpen ? 'fa-folder-open' : 'fa-folder'}`} aria-hidden="true" />
                        <span style={{ marginLeft: 6 }}>{yName}</span>
                        <div className="fault-counts" style={{ marginLeft: 'auto' }}>
                          {yNode.counts.red > 0 && <span className="count-badge red">{yNode.counts.red}</span>}
                          {yNode.counts.orange > 0 && <span className="count-badge orange">{yNode.counts.orange}</span>}
                          {yNode.counts.green > 0 && <span className="count-badge green">{yNode.counts.green}</span>}
                          {yNode.counts.purple > 0 && <span className="count-badge purple">{yNode.counts.purple}</span>}
                        </div>
                      </div>

                      {yOpen && (
                        <ul style={{ marginLeft: 28 }}>
                          {yNode.items.slice(0, 200).map(it => (
                            <li key={it.ID} onClick={() => setModalData(it)}>
                              ID-{it.ID}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}

              </div>
            );
          })}
          <div className="mahalle-divider" role="separator" aria-hidden="true" />
        </div>
      );
    });
  };


  return (
    <div className="app">
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3500,
          success: { iconTheme: { primary: '#16a34a', secondary: 'white' } },
          error: { iconTheme: { primary: '#dc2626', secondary: 'white' } }
        }}
      />
      <header className="site-header">
        <div className="brand">
          <img src="/img/logo.png" alt="İskenderun Belediyesi" className="brand-logo" />
          <div className="brand-text">
            <strong>İskenderun Belediyesi</strong>
            <small>Yol-Kaldırım Arıza Haritası</small>
          </div>
        </div>

        <nav className="main-nav"></nav>

        <div className="header-actions">
          {/* <button
            type="button"
            className="report-btn icon-square"
            aria-label="Arıza bildir"
            title="Arıza bildir"
            onClick={() => {
              const w = window.open('https://iskenderun.bel.tr/bize-bildir', '_blank', 'noopener,noreferrer');
              if (w) w.opener = null;
            }}
          >
            <i className="fas fa-bell" aria-hidden="true"></i>
          </button> */}

          <div className="header-icons">

          </div>
        </div>
      </header>



      <div id="mobileControls" className="mobile-only">
        <div className="stats-bar mobile-only" role="status" aria-label="Genel Arıza İstatistikleri">
          <div className="stat-card stat-total">
            <span className="stat-label">Toplam</span>
            <span className="stat-value">
              {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsMobile.total)}
            </span>
          </div>
          <div className="stat-card stat-red">
            <span className="stat-label">Arızalı</span>
            <span className="stat-value">
              {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsMobile.broken)}
            </span>
          </div>
          <div className="stat-card stat-orange">
            <span className="stat-label">Onarılıyor</span>
            <span className="stat-value">
              {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsMobile.inProgress)}
            </span>
          </div>
          <div className="stat-card stat-green">
            <span className="stat-label">Onarılan</span>
            <span className="stat-value">
              {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsMobile.fixed)}
            </span>
          </div>
          {/* <div className="stat-card stat-purple">
            <span className="stat-label">BİLDİRİLENLER</span>
            <span className="stat-value">
              {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsMobile.fromReport)}
            </span>
          </div> */}
        </div>

        <select
          value={selectedNeighborhood}
          onChange={(e) => {
            const m = e.target.value;
            setSelectedNeighborhood(m);
            if (m === "__ALL__" || !m) {
              setShowAllPins(true);
              setSelectedMahalles(allMahallesSet);
              setSelectedType(null);
              setSelectedYamaType(null);
              centerOnAll();
            } else {
              setShowAllPins(false);
              setSelectedMahalles(new Set([m]));
              setSelectedType(null);
              setSelectedYamaType(null);
              centerOnMahalle(m);
            }
          }}
        >
          <option value="">Mahalle seçiniz</option>
          <option value="__ALL__">Tüm Mahalleler</option>
          {Array.from(allMahallesSet).sort().map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <select
          value={selectedType ?? ALL}
          onChange={(e) => {
            const val = e.target.value;
            const nextType = (val === ALL || val === '') ? null : val;
            setSelectedType(nextType);
            setSelectedYamaType(null);

            const [m] = Array.from(selectedMahalles);
            if (m) {
              nextType ? centerOnFiltered(m, nextType, null) : centerOnMahalle(m);
            }
          }}
          disabled={selectedMahalles.size !== 1}
        >
          <option value={ALL}>GENEL (Tümü)</option>
          {selectedMahalles.size === 1 &&
            getTypesForMahalle(Array.from(selectedMahalles)[0]).map(t =>
              <option key={t} value={t}>{getTypeDisplayName(t)}</option>
            )
          }
        </select>

        <select
          value={selectedYamaType ?? ALL}
          onChange={(e) => {
            const val = e.target.value;
            const nextYama = (val === ALL || val === '') ? null : val;
            setSelectedYamaType(nextYama);

            const [m] = Array.from(selectedMahalles);
            if (m && selectedType) {
              nextYama ? centerOnFiltered(m, selectedType, nextYama)
                : centerOnFiltered(m, selectedType, null);
            }
          }}
          disabled={selectedMahalles.size !== 1 || !selectedType}
        >
          <option value={ALL}>GENEL (Tümü)</option>
          {(selectedMahalles.size === 1 && selectedType) &&
            getYamaTypesForMahalleType(Array.from(selectedMahalles)[0], selectedType).map(y =>
              <option key={y} value={y}>{y}</option>
            )
          }
        </select>

      </div>


      <div className="container">
        <aside id="sidebar" className={sidebarCollapsed ? 'collapsed' : ''}>
          <div className="sidebar-header">
            <h2>Arıza Konumları</h2>

            <button
              type="button"
              className="menu-btn"
              aria-label={sidebarCollapsed ? 'Menüyü aç' : 'Menüyü kapat'}
              aria-expanded={!sidebarCollapsed}
              onClick={() => setSidebarCollapsed(v => !v)}
              title={sidebarCollapsed ? 'Menüyü aç' : 'Menüyü kapat'}
            >
              <i className="fas fa-bars" aria-hidden="true" />
            </button>
          </div>


          <div className="sidebar-controls">
            <div className="stats-bar desktop-only" role="status" aria-label="Genel Arıza İstatistikleri">
              <div className="stat-card stat-total">
                <span className="stat-label">Toplam</span>
                <span className="stat-value">
                  {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsDesktop.total)}
                </span>
              </div>
              <div className="stat-card stat-red">
                <span className="stat-label">Arızalı</span>
                <span className="stat-value">
                  {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsDesktop.broken)}
                </span>
              </div>
              <div className="stat-card stat-orange">
                <span className="stat-label">Onarılıyor</span>
                <span className="stat-value">
                  {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsDesktop.inProgress)}
                </span>
              </div>
              <div className="stat-card stat-green">
                <span className="stat-label">Onarılan</span>
                <span className="stat-value">
                  {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsDesktop.fixed)}
                </span>
              </div>
              {/* <div className="stat-card stat-purple">
                <span className="stat-label">BİLDİRİLENLER</span>
                <span className="stat-value">
                  {isLoading ? <span className="skeleton skeleton-number" /> : nf.format(totalsDesktop.fromReport)}
                </span>
              </div> */}
            </div>

            <div className="search-container">
              <i className="fas fa-search search-icon" aria-hidden="true"></i>
              <input
                type="search"
                id="searchBox"
                placeholder="Arama"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="pin-toolbar">
              <input
                type="checkbox"
                id="pinAll"
                checked={showAllPins}
                onChange={(e) => {
                  setShowAllPins(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedMahalles(new Set());
                  } else {
                    setSelectedMahalles(allMahallesSet);
                    centerOnAll();
                  }
                }}
              />
              <label htmlFor="pinAll">Tüm Arızaları Göster</label>
            </div>

            <div className="pin-color-filter">
              <button
                type="button"
                className={`pin-filter-btn ${colorFilters.green ? 'active' : ''}`}
                data-color="green"
                onClick={() => setColorFilters(prev => ({ ...prev, green: !prev.green }))}
              >
                <img src="/img/green-pin.png" alt="Onarılmış" width="24" />
                <i className={`fas ${colorFilters.green ? 'fa-check' : 'fa-times'} status-icon`}></i>
              </button>
              <button
                type="button"
                className={`pin-filter-btn ${colorFilters.orange ? 'active' : ''}`}
                data-color="orange"
                onClick={() => setColorFilters(prev => ({ ...prev, orange: !prev.orange }))}
              >
                <img src="/img/orange-pin.png" alt="Onarılıyor" width="24" />
                <i className={`fas ${colorFilters.orange ? 'fa-check' : 'fa-times'} status-icon`}></i>
              </button>
              <button
                type="button"
                className={`pin-filter-btn ${colorFilters.red ? 'active' : ''}`}
                data-color="red"
                onClick={() => setColorFilters(prev => ({ ...prev, red: !prev.red }))}
              >
                <img src="/img/red-pin.png" alt="Arızalı" width="24" />
                <i className={`fas ${colorFilters.red ? 'fa-check' : 'fa-times'} status-icon`}></i>
              </button>
              <button
                type="button"
                className={`pin-filter-btn ${colorFilters.purple ? 'active' : ''}`}
                data-color="purple"
                onClick={() => setColorFilters(prev => ({ ...prev, purple: !prev.purple }))}
              >
                <img src="/img/purple-pin.png" alt="Bildirim" width="24" />
                <i className={`fas ${colorFilters.purple ? 'fa-check' : 'fa-times'} status-icon`}></i>
              </button>
            </div>

            <div className="ownership-toggle-group">
              <button
                type="button"
                className={`ownership-toggle ${ownershipSelection.belediye ? 'active' : ''}`}
                onClick={() => toggleOwnership('belediye')}
                title="İskenderun Belediyesi"
              >
                <img src="/img/isklogo.png" alt="İskenderun Belediyesi" />
              </button>
              <button
                type="button"
                className={`ownership-toggle ${ownershipSelection.iskent ? 'active' : ''}`}
                onClick={() => toggleOwnership('iskent')}
                title="Iskent"
              >
                <img src="/img/iskentlogo.png" alt="Iskent" />
              </button>
              <button
                type="button"
                className={`ownership-toggle insaat-toggle ${ownershipSelection.insaat ? 'active' : ''}`}
                onClick={() => toggleOwnership('insaat')}
                title="İnşaat Noktaları"
                style={{ backgroundColor: ownershipSelection.insaat ? '#d97706' : '', borderColor: ownershipSelection.insaat ? '#d97706' : '' }}
              >
                <i className="fas fa-hard-hat" style={{ color: ownershipSelection.insaat ? 'white' : '#d97706', fontSize: '20px' }}></i>
              </button>
            </div>
          </div>

          <ul id="listContainer" className="list">
            {renderSidebar()}
          </ul>

          {/* <button 
            id="togglePinSystem"
            className="sidebar-btn"
            onClick={() => setUseMarkerSystem(!useMarkerSystem)}
          >
            <img 
              src={useMarkerSystem ? "/img/glify.png" : "/img/marker.png"} 
              className="btn-icon" 
              alt=""
            />
            <span>{useMarkerSystem ? "Glify Pin" : "Marker Pin"}</span>
          </button> */}
        </aside>

        <main ref={mapRef} id="map"></main>
      </div>

      {modalData && (
        <div className="modal" onClick={() => setModalData(null)}>
          <div
            className="modal-content modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header modal-topbar">
              <h3 className="modal-title">
                Arıza Detayı <span className="id-tag">#{modalData.ID}</span>
              </h3>
              <button className="modal-close" aria-label="Kapat" onClick={() => setModalData(null)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <ul className="meta-list">
                <li className="meta-row">
                  <span className="meta-label">Konum</span>
                  <span className="meta-value">
                    {modalData.Mahalle}
                  </span>
                </li>
                <li className="meta-row">
                  <span className="meta-label">Durum</span>
                  <span className={
                    "chip " + (
                      modalData.IsFixed === 4 ? "chip--info"
                        : modalData.IsFixed === 2 ? "chip--success"
                          : modalData.IsFixed === 1 ? "chip--warning"
                            : "chip--danger"
                    )
                  }>
                    <span className="dot" />
                    {modalData.IsFixed === 4 ? "Bildirimden Gelen" :
                      modalData.IsFixed === 2 ? "Onarıldı" :
                        modalData.IsFixed === 1 ? "Onarılıyor" : "Arızalı"}
                  </span>
                </li>
                {(() => {
                  // Get ownership type from API response (CreatedByUserOwnershipType)
                  // OwnershipTypes: 1=Belediye, 2=Iskent, 3=Aksa, 4=Hatsu, 5=Enerjisa, 6=Turktelekom
                  const ownershipType = (modalData?.CreatedByUserOwnershipType ?? modalData?.createdByUserOwnershipType ?? 'Belediye').trim().toLowerCase();
                  const isIskent = ownershipType === 'iskent';
                  const isInsaat = modalData.IsInsaat || modalData.type === 'Insaat' || modalData.Type === 'Insaat' || modalData.isInsaat;

                  if ((isIskent || isInsaat) && modalData.Description) {
                    return (
                      <li className="meta-row">
                        <span className="meta-label">Açıklama</span>
                        <span className="meta-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {modalData.Description}
                        </span>
                      </li>
                    );
                  }
                  return null;
                })()}
                <li className="meta-row meta-row--navigation">
                  <span className="meta-label"></span>
                  <button
                    type="button"
                    className="navigation-btn"
                    onClick={() => {
                      const lat = modalData.Lat;
                      const lng = modalData.Lng;

                      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

                      let mapsUrl;
                      if (isIOS) {
                        mapsUrl = `http://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
                      } else {
                        mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                      }

                      const w = window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                      if (w) w.opener = null;
                    }}
                    title="Yol tarifi al"
                  >
                    <i className="fas fa-route" aria-hidden="true"></i>
                    Yol Tarifi Al
                  </button>
                </li>
              </ul>

              {modalData.ImagePath && (
                <div className="photo">
                  <button
                    type="button"
                    className="photo-zoomable"
                    onClick={() => setLightboxSrc(modalData.ImagePath)}
                    aria-label="Fotoğrafı büyüt"
                    title="Büyütmek için tıklayın"
                  >
                    <img
                      src={modalData.ImagePath}
                      alt="Arıza Fotoğrafı"
                    />
                    <span className="zoom-hint" aria-hidden="true">
                      <i className="fas fa-search-plus" />
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {lightboxSrc && (
        <div className="lightbox" onClick={() => setLightboxSrc(null)}>
          <img
            className="lightbox-img"
            src={lightboxSrc}
            alt="Büyük Görsel"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="lightbox-close"
            onClick={() => setLightboxSrc(null)}
            aria-label="Kapat"
            title="Kapat (Esc)"
          >
            ×
          </button>
        </div>
      )}
      {stackList && (
        <div className="modal" onClick={() => setStackList(null)}>
          <div
            className="modal-content modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header modal-topbar">
              <h3 className="modal-title">Bu konumda birden fazla kayıt var</h3>
              <button className="modal-close" aria-label="Kapat" onClick={() => setStackList(null)}>×</button>
            </div>

            <div className="modal-body">
              <ul className="select-list">
                {stackList.map((it) => (
                  <li key={it.ID} className="select-list-item">
                    <button
                      type="button"
                      className="select-row"
                      onClick={() => { setModalData(it); setStackList(null); }}
                      title={`#${it.ID} • ${it.Mahalle}`}
                    >
                      <span className={`dot ${it.IsFixed === 4 ? 'purple' :
                        it.IsFixed === 2 ? 'green' :
                          it.IsFixed === 1 ? 'orange' : 'red'
                        }`} />
                      <strong>#{it.ID}</strong>&nbsp;—&nbsp;{it.Mahalle}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {reportOpen && (
        <div className="modal" onClick={() => setReportOpen(false)}>
          <div
            className="modal-content modal-card report-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header modal-topbar">
              <h3 className="modal-title">Arıza Bildir</h3>
              <button className="modal-close" aria-label="Kapat" onClick={() => setReportOpen(false)}>×</button>
            </div>

            <form className="modal-body report-form" onSubmit={handleReportSubmit}>
              <div className="form-row form-row--location">
                <label className="meta-label">İsim Soyisim</label>
                <input
                  type="text"
                  placeholder="Adınız Soyadınız"
                  value={reportForm.name}
                  onChange={(e) => setReportForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-row">
                <label className="meta-label">Mahalle</label>
                <select
                  value={reportForm.mahalle}
                  onChange={(e) => setReportForm(f => ({ ...f, mahalle: e.target.value }))}
                  required
                >
                  <option value="">Seçiniz</option>
                  {mahalleList.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label className="meta-label">Görsel</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReportForm(f => ({ ...f, imageFile: e.target.files?.[0] || null }))}
                />
                {reportForm.imageFile && (
                  <small className="file-hint">
                    Seçilen dosya: <strong>{reportForm.imageFile.name}</strong>
                  </small>
                )}
              </div>

              <div className="form-row">
                <label className="meta-label">Konum</label>

                <div className="report-map-tools">
                  <button
                    type="button"
                    className="icon-square icon-square--primary"
                    title="Cihaz konumumu kullan"
                    onClick={handleUseDeviceLocation}
                  >
                    <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                  </button>
                  <div className="coords" aria-live="polite">
                    {reportForm.lat && reportForm.lng
                      ? `Lat: ${reportForm.lat.toFixed(6)}, Lng: ${reportForm.lng.toFixed(6)}`
                      : 'Haritaya tıklayarak nokta seçin veya cihaz konumunuzu kullanın.'}
                  </div>
                </div>

                <div
                  ref={reportMapContainerRef}
                  className="report-map"
                />
              </div>

              <div className="modal-footer report-footer">
                <button type="button" className="btn" onClick={() => setReportOpen(false)}>Vazgeç</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !reportForm.name || !reportForm.mahalle || !(reportForm.lat && reportForm.lng)}
                >
                  {submitting ? 'Gönderiliyor…' : 'Gönder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;

